import {Component, Input, OnInit} from '@angular/core';
import {EventEmailMessageDTO, EventService} from '../api/event-api.service';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs';
@Component({
    selector: 'app-event-emails',
    templateUrl: './event-emails.component.html',
    styleUrls: ['./event-emails.component.css']
})
export class EventEmailsComponent implements OnInit {
    @Input() eventId!: string;
    @Input() status!: string;
    @Input() chapterId: string = '';
    @Input() defaultFromEmail: string = '';
    emails: EventEmailMessageDTO[] = [];
    selectedEmail: EventEmailMessageDTO | null = null;
    newEmail: EventEmailMessageDTO = {subject: '', body: '', fromEmail: 'events@rajputra.org', additionalEmailIds: []};
    additionalEmailsInput = '';
    fromEmailOptions: string[] = [];
    loading = false;
    isEditing = false;
    testEmailRow: EventEmailMessageDTO | null = null;
    testEmail = '';
    sending = false;

    constructor(
        private eventService: EventService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit(): void {
        this.fromEmailOptions = ['events@rajputra.org', 'events@rajputra.org'];
        if (this.chapterId === '0') {
            this.fromEmailOptions.push('nec@rajputra.org');
        }
        const initialFrom = this.defaultFromEmail || 'events@rajputra.org';
        this.newEmail.fromEmail = initialFrom;
        if (this.eventId) {
            this.loadEmails();
        }
    }

    enableTestMode(email: EventEmailMessageDTO) {
        this.testEmailRow = email;
        this.testEmail = '';
    }
    cancelTestMode() {
        this.testEmailRow = null;
        this.testEmail = '';
    }
    sendTestEmail(email: EventEmailMessageDTO) {
        if (!this.testEmail) {
            this.toastr.warning('Please enter a test email address');
            return;
        }

        this.sending = true;

        this.eventService
            .sendTestEmailMessage(this.eventId, email.messageId!, this.testEmail)
            .pipe(finalize(() => (this.sending = false)))
            .subscribe({
                next: () => {
                    this.toastr.success(`Test email sent to ${this.testEmail}`);
                    this.cancelTestMode();
                },
                error: () => this.toastr.error('Failed to send test email')
            });
    }


    loadEmails() {
        this.loading = true;
        this.eventService.getAllEmailMessages(this.eventId)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (res) => (this.emails = res),
                error: () => this.toastr.error('Failed to load emails')
            });
    }

    saveEmail() {
        if (!this.newEmail.subject || !this.newEmail.body) {
            this.toastr.warning('Subject and body are required');
            return;
        }
        this.newEmail.additionalEmailIds = this.additionalEmailsInput
            ? this.additionalEmailsInput.split(',').map(e => e.trim()).filter(e => e)
            : [];

        if (this.isEditing && this.selectedEmail?.messageId) {
            // Update
            this.eventService.updateEmailMessage(this.eventId, this.selectedEmail.messageId, this.newEmail)
                .subscribe({
                    next: (res) => {
                        this.toastr.success('Email updated');
                        this.loadEmails();
                        this.cancelEdit();
                    },
                    error: () => this.toastr.error('Failed to update email')
                });
        } else {
            // Create
            this.eventService.addEmailMessage(this.eventId, this.newEmail)
                .subscribe({
                    next: (res) => {
                        this.toastr.success('Email added');
                        this.loadEmails();
                        this.resetForm();
                    },
                    error: () => this.toastr.error('Failed to add email')
                });
        }
    }

    editEmail(email: EventEmailMessageDTO) {
        this.isEditing = true;
        this.selectedEmail = email;
        this.newEmail = {...email};
        this.additionalEmailsInput = (email.additionalEmailIds || []).join(', ');
    }

    deleteEmail(email: EventEmailMessageDTO) {
        if (!confirm('Are you sure you want to delete this email?')) return;
        if (!email.messageId) return;

        this.eventService.deleteEmailMessage(this.eventId, email.messageId)
            .subscribe({
                next: () => {
                    this.toastr.success('Email deleted');
                    this.loadEmails();
                },
                error: () => this.toastr.error('Failed to delete email')
            });
    }

    sendEmail(email: EventEmailMessageDTO) {
        if (!confirm('Send this email to all event participants?')) return;

        this.eventService.sendEmailMessage(this.eventId, email.messageId + "", this.newEmail)
            .subscribe({
                next: () => this.toastr.success('Email sent'),
                error: () => this.toastr.error('Failed to send email')
            });
    }

    pushNotification(notification: EventEmailMessageDTO) {
        if (!confirm('Send this notification to all event participants?')) return;

        this.eventService.sendNotificationMessage(this.eventId)
            .subscribe({
                next: () => this.toastr.success('Notification sent'),
                error: () => this.toastr.error('Failed to send email')
            });
    }

    resetForm() {
        this.newEmail = {subject: '', body: '', fromEmail: this.defaultFromEmail || 'events@rajputra.org', additionalEmailIds: []};
        this.additionalEmailsInput = '';
        this.isEditing = false;
        this.selectedEmail = null;
    }

    cancelEdit() {
        this.resetForm();
    }

    formatDate(dateStr: string | undefined): string {
        if (!dateStr) return '';
        // Parse "MM/dd/yyyy:HH:mm:ss" format as UTC, display in local time
        const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4}):(\d{2}):(\d{2}):(\d{2})$/);
        if (!match) return dateStr;
        const [, month, day, year, hours, minutes, seconds] = match;
        const utcDate = new Date(Date.UTC(+year, +month - 1, +day, +hours, +minutes, +seconds));
        const m = utcDate.getMonth() + 1;
        const d = utcDate.getDate();
        const y = utcDate.getFullYear();
        const h = utcDate.getHours() % 12 || 12;
        const min = utcDate.getMinutes().toString().padStart(2, '0');
        const period = utcDate.getHours() >= 12 ? 'PM' : 'AM';
        return `${m.toString().padStart(2, '0')}/${d.toString().padStart(2, '0')}/${y} ${h}:${min} ${period}`;
    }

    duplicateEmail(email: EventEmailMessageDTO) {
        // Copy the email into newEmail form but reset any IDs
        this.newEmail = {subject: email.subject, body: email.body, fromEmail: email.fromEmail || 'events@rajputra.org', additionalEmailIds: email.additionalEmailIds || []};
        this.additionalEmailsInput = (email.additionalEmailIds || []).join(', ');
        this.isEditing = false;  // Important: it should be treated as a new email
        this.selectedEmail = null; // Clear selected email so saveEmail creates a new one
    }
}
